import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
    ShoppingCart, 
    Clock, 
    CheckCircle, 
    Truck, 
    XCircle,
    Package,
    Building2,
    Building,
    ChevronDown,
    ChevronUp,
    CreditCard,
    Loader2,
    AlertTriangle,
    History,
    Send,
    Printer,
    ExternalLink,
    Wallet,
    FileText,
    FileCheck,
    Upload,
    MapPin,
    Search,
    X,
    Calendar,
    RefreshCw,
    Download,
    Receipt,
    User,
    Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminOrders = () => {
    const { token } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState({});
    const [expandedOrder, setExpandedOrder] = useState(null);
    
    // Modal states
    const [shipModal, setShipModal] = useState({ open: false, orderId: null });
    const [cancelModal, setCancelModal] = useState({ open: false, orderId: null });
    const [historyModal, setHistoryModal] = useState({ open: false, orderId: null, history: [] });
    const [invoiceModal, setInvoiceModal] = useState({ open: false, orderId: null });
    const [trackingNumber, setTrackingNumber] = useState('');
    const [cancellationReason, setCancellationReason] = useState('');
    const [generatingLabel, setGeneratingLabel] = useState(false);
    const [generatingInvoice, setGeneratingInvoice] = useState(false);
    
    // Search filters
    const [searchFilters, setSearchFilters] = useState({
        orderNumber: '',
        email: '',
        company: '',
        dateFrom: '',
        dateTo: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    
    // Manual shipment state
    const [manualShipment, setManualShipment] = useState({
        carrier: '',
        trackingNumber: '',
        hasTracking: true
    });
    const [uploadingLabel, setUploadingLabel] = useState(false);
    const [finalizingOrder, setFinalizingOrder] = useState(null);
    const [fixingErpOrder, setFixingErpOrder] = useState(null);
    const [generatingProforma, setGeneratingProforma] = useState(null);
    const [enrichingOrders, setEnrichingOrders] = useState(false);

    const MANUAL_CARRIERS = [
        { value: 'chronopost', label: 'Chronopost' },
        { value: 'ups', label: 'UPS' },
        { value: 'dhl', label: 'DHL' },
        { value: 'fedex', label: 'FedEx' },
        { value: 'laposte', label: 'La Poste' },
        { value: 'colissimo', label: 'Colissimo' },
        { value: 'other', label: 'Autre' }
    ];

    const statusFilter = searchParams.get('status') || '';

    const fetchOrders = useCallback(async () => {
        try {
            const params = statusFilter ? `?status=${statusFilter}` : '';
            const response = await axios.get(`${API_URL}/admin/orders${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrders(response.data.orders);
        } catch (error) {
            console.error('Fetch orders error:', error);
            toast.error('Erreur lors du chargement des commandes');
        } finally {
            setLoading(false);
        }
    }, [token, statusFilter]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    // Enrich all orders with product details
    const enrichAllOrders = async () => {
        if (!window.confirm('Voulez-vous enrichir toutes les commandes avec les détails produits du catalogue ? Cette opération peut prendre quelques secondes.')) {
            return;
        }
        
        setEnrichingOrders(true);
        try {
            const response = await axios.post(
                `${API_URL}/admin/orders/enrich-all`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`${response.data.enriched_orders} commande(s) enrichie(s), ${response.data.total_items_enriched} article(s) mis à jour`);
            fetchOrders(); // Refresh orders
        } catch (error) {
            toast.error('Erreur lors de l\'enrichissement: ' + (error.response?.data?.detail || error.message));
        } finally {
            setEnrichingOrders(false);
        }
    };

    const generateShippingLabel = async (orderId, carrierCode) => {
        if (!carrierCode) {
            toast.error('Transporteur non spécifié');
            return;
        }
        
        setGeneratingLabel(true);
        try {
            const response = await axios.post(
                `${API_URL}/admin/orders/${orderId}/generate-shipment`,
                { carrier_code: carrierCode },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const shipment = response.data.shipment;
            toast.success(`Bon de transport généré: ${shipment.tracking_number || 'OK'}`);
            
            // Auto-fill tracking number
            if (shipment.tracking_number) {
                setTrackingNumber(shipment.tracking_number);
            }
            
            // Refresh orders to update the modal with new shipment details
            await fetchOrders();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la génération';
            toast.error(message);
        } finally {
            setGeneratingLabel(false);
        }
    };

    // Generate invoice independently
    const handleGenerateInvoice = async (orderId) => {
        setGeneratingInvoice(true);
        try {
            const response = await axios.post(
                `${API_URL}/admin/orders/${orderId}/generate-invoice`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (response.data.success) {
                toast.success(`Facture générée: ${response.data.pennylane_invoice_number || response.data.invoice_number}`);
                fetchOrders();
            }
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la génération de la facture';
            toast.error(message);
        } finally {
            setGeneratingInvoice(false);
        }
    };

    // Manual shipment
    const handleManualShipment = async (orderId) => {
        if (!manualShipment.carrier) {
            toast.error('Veuillez sélectionner un transporteur');
            return;
        }
        
        setProcessing(prev => ({ ...prev, [orderId]: true }));
        try {
            const response = await axios.post(
                `${API_URL}/admin/orders/${orderId}/manual-shipment`,
                {
                    carrier: manualShipment.carrier,
                    tracking_number: manualShipment.trackingNumber || null,
                    has_tracking: manualShipment.hasTracking
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(response.data.message);
            setShipModal({ open: false, orderId: null });
            setManualShipment({ carrier: '', trackingNumber: '', hasTracking: true });
            fetchOrders();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de l\'expédition';
            toast.error(message);
        } finally {
            setProcessing(prev => ({ ...prev, [orderId]: false }));
        }
    };

    // Upload shipping label
    const handleUploadLabel = async (orderId, file) => {
        if (!file) return;
        
        setUploadingLabel(true);
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            await axios.post(
                `${API_URL}/admin/orders/${orderId}/upload-shipping-label`,
                formData,
                { 
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    } 
                }
            );
            toast.success('Bon de transport uploadé');
            fetchOrders();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de l\'upload';
            toast.error(message);
        } finally {
            setUploadingLabel(false);
        }
    };

    const handleStatusUpdate = async (orderId, newStatus, extraData = {}) => {
        setProcessing(prev => ({ ...prev, [orderId]: true }));
        try {
            const payload = {
                status: newStatus,
                ...extraData
            };
            
            await axios.put(
                `${API_URL}/admin/orders/${orderId}/status`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Commande mise à jour: ${getStatusLabel(newStatus)}`);
            fetchOrders();
            
            // Close modals
            setShipModal({ open: false, orderId: null });
            setCancelModal({ open: false, orderId: null });
            setTrackingNumber('');
            setCancellationReason('');
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la mise à jour';
            toast.error(message);
        } finally {
            setProcessing(prev => ({ ...prev, [orderId]: false }));
        }
    };

    const fetchOrderHistory = async (orderId) => {
        try {
            const response = await axios.get(
                `${API_URL}/admin/orders/${orderId}/history`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setHistoryModal({ open: true, orderId, history: response.data.history });
        } catch (error) {
            toast.error('Erreur lors du chargement de l\'historique');
        }
    };

    const getStatusLabel = (status) => {
        const labels = {
            'pending': 'En attente',
            'pending_payment': 'Paiement en cours',
            'paid': 'Payée',
            'preparing': 'En préparation',
            'partially_shipped': 'Partiellement expédiée',
            'shipped': 'Expédiée',
            'delivered': 'Livrée',
            'cancelled': 'Annulée'
        };
        return labels[status] || status;
    };

    // Finalize a partially_shipped order
    const handleFinalizeOrder = async (orderId) => {
        setFinalizingOrder(orderId);
        try {
            const response = await axios.post(
                `${API_URL}/admin/orders/${orderId}/finalize-shipment`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(
                <div>
                    <strong>Commande finalisée</strong>
                    {response.data.pennylane_invoice_number && (
                        <p className="text-sm text-green-600">
                            Facture: {response.data.pennylane_invoice_number}
                        </p>
                    )}
                </div>
            );
            fetchOrders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la finalisation');
        } finally {
            setFinalizingOrder(null);
        }
    };

    // Fix ERP status for shipped/removed items
    const handleFixErpStatus = async (orderId) => {
        if (!window.confirm('Corriger les statuts ERP pour cette commande ?')) return;
        
        setFixingErpOrder(orderId);
        try {
            const response = await axios.post(
                `${API_URL}/admin/orders/${orderId}/fix-erp-status`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const results = response.data.results;
            toast.success(
                <div>
                    <strong>Statuts ERP corrigés</strong>
                    <p className="text-sm">
                        {results.shipped_items_fixed?.length || 0} expédiés, {results.removed_items_fixed?.length || 0} retirés
                    </p>
                </div>
            );
            fetchOrders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la correction');
        } finally {
            setFixingErpOrder(null);
        }
    };

    const getStatusBadge = (status, paymentStatus) => {
        const configs = {
            'pending': { bg: 'bg-amber-100', text: 'text-amber-800', icon: Clock, label: 'En attente paiement' },
            'pending_payment': { bg: 'bg-orange-100', text: 'text-orange-800', icon: CreditCard, label: 'Paiement en cours' },
            'paid': { bg: 'bg-blue-100', text: 'text-blue-800', icon: CheckCircle, label: 'Payée' },
            'preparing': { bg: 'bg-purple-100', text: 'text-purple-800', icon: Package, label: 'En préparation' },
            'partially_shipped': { bg: 'bg-cyan-100', text: 'text-cyan-800', icon: Truck, label: 'Partiellement expédiée' },
            'shipped': { bg: 'bg-indigo-100', text: 'text-indigo-800', icon: Truck, label: 'Expédiée' },
            'delivered': { bg: 'bg-emerald-100', text: 'text-emerald-800', icon: CheckCircle, label: 'Livrée' },
            'cancelled': { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Annulée' }
        };
        
        const config = configs[status] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: Clock, label: status };
        const Icon = config.icon;
        
        return (
            <Badge className={`${config.bg} ${config.text}`}>
                <Icon className="w-3 h-3 mr-1" />
                {config.label}
            </Badge>
        );
    };

    // Generate Proforma invoice on Pennylane
    const handleGenerateProforma = async (orderId, force = false) => {
        setGeneratingProforma(orderId);
        try {
            const response = await axios.post(
                `${API_URL}/admin/orders/${orderId}/generate-proforma`,
                { force },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (response.data.already_exists) {
                toast.info(
                    <div>
                        <strong>Proforma existante</strong>
                        <p className="text-sm">N° {response.data.proforma_number}</p>
                        {response.data.pdf_url && (
                            <a href={response.data.pdf_url} target="_blank" rel="noopener noreferrer" 
                               className="text-blue-600 underline text-sm">Voir PDF</a>
                        )}
                        <p className="text-xs text-slate-500 mt-1">Double-cliquez pour régénérer</p>
                    </div>
                );
            } else {
                toast.success(
                    <div>
                        <strong>{response.data.regenerated ? 'Proforma régénérée' : 'Proforma générée'}</strong>
                        <p className="text-sm">N° {response.data.proforma_number}</p>
                        {response.data.pdf_url && (
                            <a href={response.data.pdf_url} target="_blank" rel="noopener noreferrer" 
                               className="text-blue-600 underline text-sm">Télécharger PDF</a>
                        )}
                    </div>
                );
            }
            fetchOrders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
        } finally {
            setGeneratingProforma(null);
        }
    };

    // Force regenerate proforma (after manual deletion in Pennylane)
    const handleRegenerateProforma = async (orderId) => {
        if (!window.confirm('Régénérer la proforma ? (Utilisez cette option si vous avez supprimé la proforma manuellement dans Pennylane)')) return;
        await handleGenerateProforma(orderId, true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Workflow buttons based on current status
    const getWorkflowActions = (order) => {
        const status = order.status;
        const paymentMethod = order.payment_method;
        const paymentStatus = order.payment_status;
        const hasInvoice = order.invoice_number || order.pennylane_invoice_id;
        const actions = [];

        // Check if order is paid (stripe or credit)
        const isPaid = (paymentMethod === 'stripe' && paymentStatus === 'paid') || 
                       paymentMethod === 'credit' ||
                       status === 'paid' || status === 'preparing' || status === 'shipped' || status === 'delivered';

        // Proforma button - available before shipment
        const canGenerateProforma = ['pending', 'pending_payment', 'paid', 'preparing', 'confirmed'].includes(status);
        const hasProforma = order.pennylane_proforma_id;

        if (canGenerateProforma) {
            if (hasProforma) {
                // Two buttons: View PDF + Regenerate
                actions.push({
                    label: 'Proforma',
                    action: () => order.pennylane_proforma_pdf_url 
                        ? window.open(order.pennylane_proforma_pdf_url, '_blank')
                        : handleRegenerateProforma(order.id),
                    icon: FileCheck,
                    variant: 'outline',
                    className: 'border-green-300 text-green-700 hover:bg-green-50',
                    loading: generatingProforma === order.id
                });
                // Add regenerate option
                actions.push({
                    label: 'Régénérer',
                    action: () => handleRegenerateProforma(order.id),
                    icon: RefreshCw,
                    variant: 'outline',
                    className: 'border-amber-300 text-amber-700 hover:bg-amber-50',
                    loading: generatingProforma === order.id
                });
            } else {
                actions.push({
                    label: 'Générer Proforma',
                    action: () => handleGenerateProforma(order.id),
                    icon: FileCheck,
                    variant: 'outline',
                    className: 'border-blue-300 text-blue-700 hover:bg-blue-50',
                    loading: generatingProforma === order.id
                });
            }
        }

        if (status === 'pending' || status === 'pending_payment') {
            actions.push({
                label: 'Marquer payée',
                status: 'paid',
                icon: CreditCard,
                variant: 'default',
                className: 'bg-blue-600 hover:bg-blue-700'
            });
            actions.push({
                label: 'Annuler',
                action: () => setCancelModal({ open: true, orderId: order.id }),
                icon: XCircle,
                variant: 'outline',
                className: 'text-red-600 border-red-200 hover:bg-red-50'
            });
        }

        if (status === 'paid') {
            // Invoice button - only for paid orders
            if (!hasInvoice) {
                actions.push({
                    label: 'Générer Facture',
                    action: () => handleGenerateInvoice(order.id),
                    icon: FileText,
                    variant: 'default',
                    className: 'bg-green-600 hover:bg-green-700'
                });
            }
            actions.push({
                label: 'En préparation',
                status: 'preparing',
                icon: Package,
                variant: 'default',
                className: 'bg-purple-600 hover:bg-purple-700'
            });
            actions.push({
                label: 'Annuler',
                action: () => setCancelModal({ open: true, orderId: order.id }),
                icon: XCircle,
                variant: 'outline',
                className: 'text-red-600 border-red-200 hover:bg-red-50'
            });
        }

        if (status === 'preparing') {
            // Invoice button - only for paid orders that don't have invoice yet
            if (!hasInvoice && isPaid) {
                actions.push({
                    label: 'Générer Facture',
                    action: () => handleGenerateInvoice(order.id),
                    icon: FileText,
                    variant: 'default',
                    className: 'bg-green-600 hover:bg-green-700'
                });
            }
            // No "Expédier" button here - shipping is done in /admin/preparation page
            actions.push({
                label: 'Annuler',
                action: () => setCancelModal({ open: true, orderId: order.id }),
                icon: XCircle,
                variant: 'outline',
                className: 'text-red-600 border-red-200 hover:bg-red-50'
            });
        }

        if (status === 'shipped') {
            actions.push({
                label: 'Marquer livrée',
                status: 'delivered',
                icon: CheckCircle,
                variant: 'default',
                className: 'bg-emerald-600 hover:bg-emerald-700'
            });
        }

        // For partially_shipped orders - check if all items are shipped
        if (status === 'partially_shipped') {
            const totalItems = order.items?.length || 0;
            const shippedImeis = (order.shipments || []).flatMap(s => s.items || []);
            const shippedCount = shippedImeis.length;
            const allShipped = shippedCount >= totalItems;
            
            if (allShipped) {
                actions.push({
                    label: 'Finaliser & Facturer',
                    action: () => handleFinalizeOrder(order.id),
                    icon: Receipt,
                    variant: 'default',
                    className: 'bg-green-600 hover:bg-green-700',
                    loading: finalizingOrder === order.id
                });
            }
            actions.push({
                label: 'Marquer livrée',
                status: 'delivered',
                icon: CheckCircle,
                variant: 'default',
                className: 'bg-emerald-600 hover:bg-emerald-700'
            });
        }

        // Fix ERP status button for shipped/partially_shipped orders with removals
        if (['shipped', 'partially_shipped', 'delivered'].includes(status) && 
            (order.removal_history?.length > 0 || order.shipments?.length > 0)) {
            actions.push({
                label: 'Corriger ERP',
                action: () => handleFixErpStatus(order.id),
                icon: Zap,
                variant: 'outline',
                className: 'border-amber-300 text-amber-700 hover:bg-amber-50',
                loading: fixingErpOrder === order.id
            });
        }

        return actions;
    };

    const statusOptions = [
        { value: 'all', label: 'Tous les statuts' },
        { value: 'pending', label: 'En attente' },
        { value: 'pending_payment', label: 'Paiement en cours' },
        { value: 'paid', label: 'Payées' },
        { value: 'preparing', label: 'En préparation' },
        { value: 'partially_shipped', label: 'Partiellement expédiées' },
        { value: 'shipped', label: 'Expédiées' },
        { value: 'delivered', label: 'Livrées' },
        { value: 'cancelled', label: 'Annulées' }
    ];

    // Count orders by status
    const statusCounts = orders.reduce((acc, order) => {
        acc[order.status] = (acc[order.status] || 0) + 1;
        return acc;
    }, {});

    // Filter orders based on search criteria
    const filteredOrders = orders.filter(order => {
        // Filter by order number
        if (searchFilters.orderNumber) {
            const searchTerm = searchFilters.orderNumber.toLowerCase().replace('#', '');
            if (!order.id.toLowerCase().includes(searchTerm)) {
                return false;
            }
        }
        
        // Filter by email
        if (searchFilters.email) {
            if (!order.user_email?.toLowerCase().includes(searchFilters.email.toLowerCase())) {
                return false;
            }
        }
        
        // Filter by company
        if (searchFilters.company) {
            if (!order.company_name?.toLowerCase().includes(searchFilters.company.toLowerCase())) {
                return false;
            }
        }
        
        // Filter by date range
        if (searchFilters.dateFrom) {
            const orderDate = new Date(order.created_at);
            const fromDate = new Date(searchFilters.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            if (orderDate < fromDate) {
                return false;
            }
        }
        
        if (searchFilters.dateTo) {
            const orderDate = new Date(order.created_at);
            const toDate = new Date(searchFilters.dateTo);
            toDate.setHours(23, 59, 59, 999);
            if (orderDate > toDate) {
                return false;
            }
        }
        
        return true;
    });

    // Check if any filter is active
    const hasActiveFilters = searchFilters.orderNumber || searchFilters.email || 
                             searchFilters.company || searchFilters.dateFrom || searchFilters.dateTo;

    // Clear all filters
    const clearFilters = () => {
        setSearchFilters({
            orderNumber: '',
            email: '',
            company: '',
            dateFrom: '',
            dateTo: ''
        });
    };

    return (
        <AdminLayout>
            <div className="p-6 lg:p-8">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                            Gestion des commandes
                        </h1>
                        <p className="text-slate-600 mt-1">
                            {orders.length} commande(s) au total
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Enrich Orders Button */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={enrichAllOrders}
                            disabled={enrichingOrders}
                            className="text-purple-600 border-purple-200 hover:bg-purple-50"
                            title="Enrichir les anciennes commandes avec les détails produits (grade, état, lock, etc.)"
                        >
                            {enrichingOrders ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <RefreshCw className="w-4 h-4 mr-2" />
                            )}
                            Enrichir commandes
                        </Button>

                        <Select 
                            value={statusFilter || 'all'} 
                            onValueChange={(value) => setSearchParams(value && value !== 'all' ? { status: value } : {})}
                        >
                            <SelectTrigger className="w-52" data-testid="order-status-filter">
                                <SelectValue placeholder="Tous les statuts" />
                            </SelectTrigger>
                            <SelectContent>
                                {statusOptions.map((opt) => (
                                    <SelectItem key={opt.value || 'all'} value={opt.value}>
                                        {opt.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Status Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
                    {[
                        { status: 'pending', label: 'En attente', color: 'amber' },
                        { status: 'pending_payment', label: 'Paiement', color: 'orange' },
                        { status: 'paid', label: 'Payées', color: 'blue' },
                        { status: 'preparing', label: 'Préparation', color: 'purple' },
                        { status: 'shipped', label: 'Expédiées', color: 'indigo' },
                        { status: 'delivered', label: 'Livrées', color: 'emerald' },
                        { status: 'cancelled', label: 'Annulées', color: 'red' }
                    ].map(({ status, label, color }) => (
                        <button
                            key={status}
                            onClick={() => setSearchParams(statusFilter === status ? {} : { status })}
                            className={`p-3 rounded-lg border transition-all ${
                                statusFilter === status 
                                    ? `bg-${color}-100 border-${color}-300` 
                                    : 'bg-white border-slate-200 hover:border-slate-300'
                            }`}
                        >
                            <div className={`text-2xl font-bold text-${color}-600`}>
                                {statusCounts[status] || 0}
                            </div>
                            <div className="text-xs text-slate-600 truncate">{label}</div>
                        </button>
                    ))}
                </div>

                {/* Search Filters */}
                <Card className="mb-6 border-slate-200">
                    <CardContent className="p-4">
                        {/* Toggle + Quick Search */}
                        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                            <div className="flex items-center gap-3 flex-1 w-full md:w-auto">
                                <div className="relative flex-1 max-w-md">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        placeholder="Rechercher par n° commande..."
                                        value={searchFilters.orderNumber}
                                        onChange={(e) => setSearchFilters(prev => ({ ...prev, orderNumber: e.target.value }))}
                                        className="pl-10 h-10"
                                        data-testid="search-order-number"
                                    />
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowFilters(!showFilters)}
                                    className={`flex items-center gap-2 ${showFilters ? 'bg-blue-50 border-blue-300' : ''}`}
                                >
                                    <Search className="w-4 h-4" />
                                    Filtres avancés
                                    {hasActiveFilters && (
                                        <Badge className="bg-blue-600 text-white text-xs px-1.5">
                                            {[searchFilters.orderNumber, searchFilters.email, searchFilters.company, searchFilters.dateFrom, searchFilters.dateTo].filter(Boolean).length}
                                        </Badge>
                                    )}
                                </Button>
                                {hasActiveFilters && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={clearFilters}
                                        className="text-slate-500 hover:text-red-600"
                                    >
                                        <X className="w-4 h-4 mr-1" />
                                        Effacer
                                    </Button>
                                )}
                            </div>
                            <div className="text-sm text-slate-500">
                                {filteredOrders.length} / {orders.length} commande(s)
                            </div>
                        </div>

                        {/* Advanced Filters */}
                        {showFilters && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-200">
                                <div>
                                    <Label className="text-xs text-slate-600 mb-1.5 block">Email client</Label>
                                    <Input
                                        placeholder="exemple@email.com"
                                        value={searchFilters.email}
                                        onChange={(e) => setSearchFilters(prev => ({ ...prev, email: e.target.value }))}
                                        className="h-9"
                                        data-testid="search-email"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-slate-600 mb-1.5 block">Nom entreprise</Label>
                                    <Input
                                        placeholder="Nom de l'entreprise"
                                        value={searchFilters.company}
                                        onChange={(e) => setSearchFilters(prev => ({ ...prev, company: e.target.value }))}
                                        className="h-9"
                                        data-testid="search-company"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-slate-600 mb-1.5 block">Date début</Label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <Input
                                            type="date"
                                            value={searchFilters.dateFrom}
                                            onChange={(e) => setSearchFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                                            className="h-9 pl-10"
                                            data-testid="search-date-from"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs text-slate-600 mb-1.5 block">Date fin</Label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <Input
                                            type="date"
                                            value={searchFilters.dateTo}
                                            onChange={(e) => setSearchFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                                            className="h-9 pl-10"
                                            data-testid="search-date-to"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Orders List */}
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                ) : filteredOrders.length === 0 ? (
                    <div className="text-center py-16">
                        <ShoppingCart className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                        <h2 className="text-xl font-semibold text-slate-900 mb-2">
                            {hasActiveFilters ? 'Aucun résultat' : 'Aucune commande'}
                        </h2>
                        <p className="text-slate-600">
                            {hasActiveFilters 
                                ? 'Aucune commande ne correspond aux critères de recherche' 
                                : 'Aucune commande ne correspond aux filtres'}
                        </p>
                        {hasActiveFilters && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={clearFilters}
                                className="mt-4"
                            >
                                Effacer les filtres
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredOrders.map((order) => (
                            <Card key={order.id} className="border-slate-200" data-testid={`order-${order.id}`}>
                                <CardContent className="p-6">
                                    {/* Header */}
                                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                                        <div className="flex items-start gap-4">
                                            <div>
                                                <div className="flex items-center gap-3 flex-wrap">
                                                    <span className="font-mono font-bold text-slate-900 text-lg">
                                                        #{order.id.slice(0, 8)}
                                                    </span>
                                                    {getStatusBadge(order.status, order.payment_status)}
                                                    {order.payment_method === 'stripe' && (
                                                        <Badge variant="outline" className="text-xs">
                                                            <CreditCard className="w-3 h-3 mr-1" />
                                                            Stripe
                                                        </Badge>
                                                    )}
                                                    {order.payment_method === 'bank_transfer' && (
                                                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                                            <Building className="w-3 h-3 mr-1" />
                                                            Virement
                                                        </Badge>
                                                    )}
                                                    {order.payment_method === 'bank_transfer' && order.instant_transfer_signaled && (
                                                        <Badge className="text-xs bg-blue-100 text-blue-700 border-blue-300 animate-pulse">
                                                            <Zap className="w-3 h-3 mr-1" />
                                                            Virement immédiat signalé
                                                        </Badge>
                                                    )}
                                                    {order.credit_applied > 0 && (
                                                        <Badge className="text-xs bg-green-100 text-green-700 border-green-200">
                                                            <Wallet className="w-3 h-3 mr-1" />
                                                            Crédit: {order.credit_applied.toFixed(2)}€
                                                        </Badge>
                                                    )}
                                                    {order.credit_applied > 0 && order.total_ttc === order.credit_applied && (
                                                        <Badge className="text-xs bg-emerald-100 text-emerald-700">
                                                            100% Crédit
                                                        </Badge>
                                                    )}
                                                    {/* Invoice badge - clickable to download */}
                                                    {(order.invoice_number || order.pennylane_invoice_id) && (
                                                        <button
                                                            onClick={async () => {
                                                                try {
                                                                    toast.info('Téléchargement de la facture...');
                                                                    // Always use our API to get the invoice
                                                                    const response = await axios.get(
                                                                        `${API_URL}/admin/orders/${order.id}/invoice`,
                                                                        {
                                                                            headers: { Authorization: `Bearer ${token}` },
                                                                            responseType: 'blob'
                                                                        }
                                                                    );
                                                                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                                                                    const link = document.createElement('a');
                                                                    link.href = url;
                                                                    link.setAttribute('download', `facture_${order.pennylane_invoice_number || order.invoice_number}.pdf`);
                                                                    document.body.appendChild(link);
                                                                    link.click();
                                                                    link.remove();
                                                                    window.URL.revokeObjectURL(url);
                                                                    toast.success('Facture téléchargée');
                                                                } catch (error) {
                                                                    console.error('Invoice download error:', error);
                                                                    toast.error(error.response?.data?.detail || 'Erreur téléchargement facture');
                                                                }
                                                            }}
                                                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200 transition-colors cursor-pointer"
                                                        >
                                                            <Download className="w-3 h-3" />
                                                            {order.pennylane_invoice_number || order.invoice_number}
                                                        </button>
                                                    )}
                                                </div>
                                                <p className="text-sm text-slate-500 mt-1">
                                                    Créée le {formatDate(order.created_at)}
                                                </p>
                                            </div>
                                            
                                            {/* Quick document buttons */}
                                            <div className="flex gap-2">
                                                {/* Bon de commande */}
                                                <button
                                                    onClick={async () => {
                                                        try {
                                                            toast.info('Téléchargement du bon de commande...');
                                                            const response = await axios.get(
                                                                `${API_URL}/admin/orders/${order.id}/packing-slip`,
                                                                {
                                                                    headers: { Authorization: `Bearer ${token}` },
                                                                    responseType: 'blob'
                                                                }
                                                            );
                                                            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                                                            const link = document.createElement('a');
                                                            link.href = url;
                                                            link.setAttribute('download', `bon_commande_${order.id.slice(0, 8)}.pdf`);
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            link.remove();
                                                            window.URL.revokeObjectURL(url);
                                                            toast.success('Bon de commande téléchargé');
                                                        } catch (error) {
                                                            console.error('Download error:', error);
                                                            toast.error(error.response?.data?.detail || 'Erreur téléchargement');
                                                        }
                                                    }}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-xs font-medium"
                                                    title="Télécharger le bon de commande"
                                                >
                                                    <Receipt className="w-4 h-4" />
                                                    Bon de commande
                                                </button>
                                            </div>
                                        </div>

                                        {/* Workflow Actions */}
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {getWorkflowActions(order).map((action, idx) => (
                                                <Button
                                                    key={idx}
                                                    variant={action.variant}
                                                    size="sm"
                                                    className={`rounded-lg ${action.className}`}
                                                    disabled={processing[order.id] || action.loading}
                                                    onClick={() => action.action ? action.action() : handleStatusUpdate(order.id, action.status)}
                                                    data-testid={`action-${action.status || 'custom'}-${order.id}`}
                                                >
                                                    {(processing[order.id] || action.loading) ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <>
                                                            <action.icon className="w-4 h-4 mr-1" />
                                                            {action.label}
                                                        </>
                                                    )}
                                                </Button>
                                            ))}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => fetchOrderHistory(order.id)}
                                                className="text-slate-500"
                                            >
                                                <History className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Client Info */}
                                    <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
                                        <div className="flex items-center gap-2">
                                            <Building2 className="w-4 h-4 text-slate-400" />
                                            <span className="font-medium">{order.company_name}</span>
                                        </div>
                                        <span className="text-slate-300">|</span>
                                        <span className="text-slate-600">{order.user_email}</span>
                                        <span className="text-slate-300">|</span>
                                        <span className="text-slate-600">
                                            {order.shipping_method === 'pickup' ? 'Retrait sur place' : 
                                             order.shipping_method === 'express' ? 'Livraison express' : 'Livraison standard'}
                                        </span>
                                    </div>

                                    {/* Tracking & Dates */}
                                    {(order.tracking_number || order.shipped_at || order.delivered_at || order.shipment?.reference || order.shipping_method_manual || order.has_tracking === false) && (
                                        <div className="flex flex-wrap items-center gap-4 mb-4 text-sm bg-slate-50 rounded-lg p-3">
                                            {/* Manual carrier info */}
                                            {order.shipping_method_manual && (
                                                <div className="flex items-center gap-2">
                                                    <Badge className={order.has_tracking === false ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
                                                        <Truck className="w-3 h-3 mr-1" />
                                                        {order.manual_carrier_name || order.manual_carrier}
                                                    </Badge>
                                                    {order.has_tracking === false && (
                                                        <span className="text-xs text-amber-600 font-medium">Sans suivi</span>
                                                    )}
                                                </div>
                                            )}
                                            {order.tracking_number && (
                                                <div>
                                                    <span className="text-slate-500">N° Suivi: </span>
                                                    <span className="font-mono font-medium">{order.tracking_number}</span>
                                                </div>
                                            )}
                                            {order.shipment?.reference && (
                                                <button 
                                                    onClick={async () => {
                                                        try {
                                                            toast.info('Téléchargement du bon de transport...');
                                                            const response = await axios.get(
                                                                `${API_URL}/admin/orders/${order.id}/label`,
                                                                {
                                                                    headers: { Authorization: `Bearer ${token}` },
                                                                    responseType: 'blob'
                                                                }
                                                            );
                                                            
                                                            // Check if the response is actually a PDF (not an error JSON)
                                                            const contentType = response.headers['content-type'] || '';
                                                            if (contentType.includes('application/json') || response.data.size < 100) {
                                                                // It's an error response, read the blob as text
                                                                const text = await response.data.text();
                                                                try {
                                                                    const errorData = JSON.parse(text);
                                                                    toast.error(errorData.detail || 'Erreur lors du téléchargement');
                                                                } catch {
                                                                    toast.error('Le bon de transport n\'est pas encore disponible. Réessayez dans quelques minutes.');
                                                                }
                                                                return;
                                                            }
                                                            
                                                            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                                                            const link = document.createElement('a');
                                                            link.href = url;
                                                            link.setAttribute('download', `bordereau_${order.shipment?.reference || order.id}.pdf`);
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            link.remove();
                                                            window.URL.revokeObjectURL(url);
                                                            toast.success('Bon de transport téléchargé !');
                                                        } catch (error) {
                                                            console.error('Download error:', error);
                                                            // Try to extract error message from blob
                                                            if (error.response?.data instanceof Blob) {
                                                                try {
                                                                    const text = await error.response.data.text();
                                                                    const errorData = JSON.parse(text);
                                                                    toast.error(errorData.detail || 'Erreur téléchargement');
                                                                } catch {
                                                                    toast.error('Erreur lors du téléchargement. Réessayez.');
                                                                }
                                                            } else {
                                                                toast.error(error.response?.data?.detail || 'Erreur téléchargement');
                                                            }
                                                        }
                                                    }}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-xs font-medium cursor-pointer"
                                                >
                                                    <Printer className="w-3.5 h-3.5" />
                                                    Imprimer le bon
                                                </button>
                                            )}
                                            {order.tracking_url && order.has_tracking !== false && (
                                                <a 
                                                    href={order.tracking_url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-xs font-medium"
                                                >
                                                    <ExternalLink className="w-3.5 h-3.5" />
                                                    Suivre le colis
                                                </a>
                                            )}
                                            {order.shipped_at && (
                                                <div>
                                                    <span className="text-slate-500">Expédiée le: </span>
                                                    <span>{formatDate(order.shipped_at)}</span>
                                                </div>
                                            )}
                                            {order.delivered_at && (
                                                <div>
                                                    <span className="text-slate-500">Livrée le: </span>
                                                    <span>{formatDate(order.delivered_at)}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Summary */}
                                    <div className="flex flex-wrap items-center gap-6 text-sm">
                                        <div className="flex items-center gap-2">
                                            <Package className="w-4 h-4 text-slate-400" />
                                            <span>{order.items.length} article(s)</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Total HT: </span>
                                            <span className="font-medium">{order.total_ht?.toFixed(2)} €</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">TVA: </span>
                                            <span className="font-medium">{order.total_vat?.toFixed(2)} €</span>
                                        </div>
                                        <div className="text-lg font-bold text-slate-900">
                                            {order.total_ttc?.toFixed(2)} € TTC
                                        </div>
                                    </div>

                                    {/* Cancellation reason if cancelled */}
                                    {order.status === 'cancelled' && order.cancellation_reason && (
                                        <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                                            <p className="text-sm font-medium text-red-800">Motif d'annulation:</p>
                                            <p className="text-sm text-red-700">{order.cancellation_reason}</p>
                                        </div>
                                    )}

                                    {/* Expand/Collapse Items */}
                                    <button
                                        className="flex items-center gap-2 mt-4 text-sm text-blue-600 hover:text-blue-800"
                                        onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                                    >
                                        {expandedOrder === order.id ? (
                                            <>
                                                <ChevronUp className="w-4 h-4" />
                                                Masquer les détails
                                            </>
                                        ) : (
                                            <>
                                                <ChevronDown className="w-4 h-4" />
                                                Voir les détails ({order.items.length} articles)
                                            </>
                                        )}
                                    </button>

                                    {/* Expanded Items */}
                                    {expandedOrder === order.id && (
                                        <div className="mt-4 pt-4 border-t border-slate-200">
                                            <div className="grid gap-2">
                                                {order.items.map((item, index) => (
                                                    <div key={index} className="flex justify-between items-center text-sm bg-slate-50 rounded px-4 py-2">
                                                        <div>
                                                            <span className="font-medium">{item.brand} {item.model}</span>
                                                            <span className="text-slate-500 ml-2 font-mono text-xs">
                                                                IMEI: {item.imei}
                                                            </span>
                                                            <Badge variant="outline" className="ml-2 text-xs">
                                                                {item.vat_regime === 'margin' ? 'Marge' : 'TVA'}
                                                            </Badge>
                                                        </div>
                                                        <span className="font-medium">{item.price?.toFixed(2)} €</span>
                                                    </div>
                                                ))}
                                            </div>

                                            {order.notes && (
                                                <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                                                    <p className="text-sm font-medium text-amber-800">Notes client:</p>
                                                    <p className="text-sm text-amber-700">{order.notes}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Ship Modal */}
            <Dialog open={shipModal.open} onOpenChange={(open) => {
                if (!open) {
                    setShipModal({ open: false, orderId: null });
                    setTrackingNumber('');
                    setManualShipment({ carrier: '', trackingNumber: '', hasTracking: true });
                }
            }}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Truck className="w-5 h-5 text-indigo-600" />
                            Préparer l'expédition
                        </DialogTitle>
                    </DialogHeader>
                    {(() => {
                        const order = orders.find(o => o.id === shipModal.orderId);
                        const clientCarrier = order?.shipping_method;
                        const clientAddress = order?.delivery_address;
                        
                        return (
                            <div className="space-y-6 py-4">
                                {/* Delivery address */}
                                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                                    <h4 className="font-medium text-slate-700 flex items-center gap-2">
                                        <MapPin className="w-4 h-4" />
                                        Adresse de livraison
                                    </h4>
                                    {clientAddress ? (
                                        <div className="text-sm text-slate-600">
                                            <p className="font-medium">{clientAddress.name || order?.company_name}</p>
                                            <p>{clientAddress.street}</p>
                                            {clientAddress.street2 && <p>{clientAddress.street2}</p>}
                                            <p>{clientAddress.postal_code} {clientAddress.city}</p>
                                            <p>{clientAddress.country}</p>
                                            {clientAddress.phone && <p>Tél: {clientAddress.phone}</p>}
                                        </div>
                                    ) : (
                                        <p className="text-amber-600">Adresse non renseignée</p>
                                    )}
                                </div>

                                {/* Two options: Boxtal or Manual */}
                                <div className="grid md:grid-cols-2 gap-4">
                                    {/* Option 1: Boxtal */}
                                    <div className="border-2 border-indigo-200 rounded-xl p-4 space-y-3 bg-indigo-50/30">
                                        <h4 className="font-semibold text-indigo-800 flex items-center gap-2">
                                            <Package className="w-5 h-5" />
                                            Option 1: Via Boxtal
                                        </h4>
                                        <p className="text-sm text-slate-600">
                                            Transporteur client: <strong>{clientCarrier || 'Non spécifié'}</strong>
                                        </p>
                                        
                                        {order?.shipment?.reference ? (
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 text-green-600 text-sm">
                                                    <CheckCircle className="w-4 h-4" />
                                                    <span>Bon généré</span>
                                                </div>
                                                
                                                {/* Shipment details */}
                                                <div className="bg-white rounded border p-2 text-xs space-y-1">
                                                    <p><span className="text-slate-500">Réf Boxtal:</span> <span className="font-mono">{order.shipment.reference}</span></p>
                                                    {order.tracking_number && (
                                                        <p><span className="text-slate-500">N° Suivi:</span> <span className="font-mono font-medium">{order.tracking_number}</span></p>
                                                    )}
                                                    {order.tracking_url && (
                                                        <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline flex items-center gap-1">
                                                            Suivre le colis <ExternalLink className="w-3 h-3" />
                                                        </a>
                                                    )}
                                                </div>
                                                
                                                <Button
                                                    size="sm"
                                                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                                                    onClick={async () => {
                                                        try {
                                                            const response = await axios.get(
                                                                `${API_URL}/admin/orders/${shipModal.orderId}/label`,
                                                                { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
                                                            );
                                                            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                                                            const link = document.createElement('a');
                                                            link.href = url;
                                                            link.setAttribute('download', `bordereau_${order?.tracking_number || shipModal.orderId}.pdf`);
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            link.remove();
                                                            toast.success('Bon téléchargé');
                                                        } catch (error) {
                                                            toast.error('Erreur téléchargement');
                                                        }
                                                    }}
                                                >
                                                    <Printer className="w-4 h-4 mr-1" />
                                                    Imprimer Bordereau
                                                </Button>
                                            </div>
                                        ) : (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="w-full"
                                                disabled={generatingLabel || !clientCarrier || clientCarrier === 'PICKUP'}
                                                onClick={() => generateShippingLabel(shipModal.orderId, clientCarrier)}
                                            >
                                                {generatingLabel ? (
                                                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                                ) : (
                                                    <Package className="w-4 h-4 mr-1" />
                                                )}
                                                Générer Boxtal
                                            </Button>
                                        )}
                                        
                                        {order?.shipment?.reference && (
                                            <div className="pt-2 border-t">
                                                <Label className="text-xs text-slate-600">N° Suivi (auto)</Label>
                                                <Input
                                                    value={trackingNumber}
                                                    onChange={(e) => setTrackingNumber(e.target.value)}
                                                    placeholder="Rempli automatiquement"
                                                    className="mt-1 text-sm"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Option 2: Manual */}
                                    <div className="border-2 border-amber-200 rounded-xl p-4 space-y-3 bg-amber-50/30">
                                        <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                                            <Upload className="w-5 h-5" />
                                            Option 2: Expédition Manuelle
                                        </h4>
                                        <p className="text-xs text-slate-600">
                                            Upload votre propre bon ou expédiez sans Boxtal
                                        </p>
                                        
                                        {/* Carrier selection */}
                                        <div>
                                            <Label className="text-xs">Transporteur *</Label>
                                            <Select 
                                                value={manualShipment.carrier} 
                                                onValueChange={(val) => setManualShipment(prev => ({ ...prev, carrier: val }))}
                                            >
                                                <SelectTrigger className="mt-1">
                                                    <SelectValue placeholder="Choisir..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {MANUAL_CARRIERS.map(c => (
                                                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* Has tracking checkbox */}
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="hasTracking"
                                                checked={manualShipment.hasTracking}
                                                onChange={(e) => setManualShipment(prev => ({ ...prev, hasTracking: e.target.checked }))}
                                                className="rounded border-slate-300"
                                            />
                                            <Label htmlFor="hasTracking" className="text-sm cursor-pointer">
                                                Avec numéro de suivi
                                            </Label>
                                        </div>

                                        {manualShipment.hasTracking && (
                                            <div>
                                                <Label className="text-xs">N° de suivi</Label>
                                                <Input
                                                    value={manualShipment.trackingNumber}
                                                    onChange={(e) => setManualShipment(prev => ({ ...prev, trackingNumber: e.target.value }))}
                                                    placeholder="Ex: 1Z999AA10123456784"
                                                    className="mt-1 text-sm"
                                                />
                                            </div>
                                        )}

                                        {!manualShipment.hasTracking && (
                                            <div className="p-2 bg-amber-100 rounded text-xs text-amber-800">
                                                ⚠️ Le client ne pourra pas suivre son colis
                                            </div>
                                        )}

                                        {/* Upload label (optional) */}
                                        <div>
                                            <Label className="text-xs">Bon de transport (PDF, optionnel)</Label>
                                            <div className="mt-1 flex items-center gap-2">
                                                <Input
                                                    type="file"
                                                    accept=".pdf"
                                                    onChange={(e) => {
                                                        if (e.target.files?.[0]) {
                                                            handleUploadLabel(shipModal.orderId, e.target.files[0]);
                                                        }
                                                    }}
                                                    className="text-xs"
                                                    disabled={uploadingLabel}
                                                />
                                                {uploadingLabel && <Loader2 className="w-4 h-4 animate-spin" />}
                                            </div>
                                            {order?.manual_shipping_label && (
                                                <p className="text-xs text-green-600 mt-1">✓ Bon uploadé</p>
                                            )}
                                        </div>

                                        <Button
                                            size="sm"
                                            className="w-full bg-amber-600 hover:bg-amber-700"
                                            disabled={!manualShipment.carrier || processing[shipModal.orderId]}
                                            onClick={() => handleManualShipment(shipModal.orderId)}
                                        >
                                            {processing[shipModal.orderId] ? (
                                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                            ) : (
                                                <Send className="w-4 h-4 mr-1" />
                                            )}
                                            Expédier {!manualShipment.hasTracking ? '(sans suivi)' : ''}
                                        </Button>
                                    </div>
                                </div>

                                {/* Info */}
                                <p className="text-xs text-slate-500 text-center">
                                    L'ERP sera notifié. Le client recevra une confirmation par email.
                                </p>
                            </div>
                        );
                    })()}
                    <DialogFooter className="border-t pt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShipModal({ open: false, orderId: null });
                                setTrackingNumber('');
                                setManualShipment({ carrier: '', trackingNumber: '', hasTracking: true });
                            }}
                        >
                            Fermer
                        </Button>
                        {orders.find(o => o.id === shipModal.orderId)?.shipment?.reference && (
                            <Button
                                className="bg-indigo-600 hover:bg-indigo-700"
                                disabled={processing[shipModal.orderId]}
                                onClick={() => handleStatusUpdate(shipModal.orderId, 'shipped', { tracking_number: trackingNumber || null })}
                            >
                                {processing[shipModal.orderId] ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                    <Send className="w-4 h-4 mr-2" />
                                )}
                                Confirmer via Boxtal
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cancel Modal */}
            <Dialog open={cancelModal.open} onOpenChange={(open) => !open && setCancelModal({ open: false, orderId: null })}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Annuler la commande
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                            <p className="text-sm text-red-800">
                                <strong>Attention:</strong> Cette action remettra les produits en stock et notifiera l'ERP de l'annulation. Un email sera envoyé au client.
                            </p>
                        </div>
                        
                        {/* Predefined reasons */}
                        <div>
                            <Label className="mb-2 block">Raisons fréquentes</Label>
                            <div className="space-y-2">
                                <Button
                                    type="button"
                                    variant={cancellationReason === "Stock indisponible - Le produit a été vendu à un autre client avant la finalisation de votre paiement." ? "default" : "outline"}
                                    className="w-full justify-start text-left h-auto py-2 px-3"
                                    onClick={() => setCancellationReason("Stock indisponible - Le produit a été vendu à un autre client avant la finalisation de votre paiement.")}
                                >
                                    <Package className="w-4 h-4 mr-2 flex-shrink-0" />
                                    <span className="text-sm">Stock indisponible - Produit vendu à un autre client</span>
                                </Button>
                                <Button
                                    type="button"
                                    variant={cancellationReason === "Délai de paiement dépassé - Votre commande a été annulée car nous n'avons pas reçu votre paiement dans le délai imparti." ? "default" : "outline"}
                                    className="w-full justify-start text-left h-auto py-2 px-3"
                                    onClick={() => setCancellationReason("Délai de paiement dépassé - Votre commande a été annulée car nous n'avons pas reçu votre paiement dans le délai imparti.")}
                                >
                                    <Clock className="w-4 h-4 mr-2 flex-shrink-0" />
                                    <span className="text-sm">Délai de paiement dépassé</span>
                                </Button>
                                <Button
                                    type="button"
                                    variant={cancellationReason === "Annulation à la demande du client." ? "default" : "outline"}
                                    className="w-full justify-start text-left h-auto py-2 px-3"
                                    onClick={() => setCancellationReason("Annulation à la demande du client.")}
                                >
                                    <User className="w-4 h-4 mr-2 flex-shrink-0" />
                                    <span className="text-sm">Annulation à la demande du client</span>
                                </Button>
                            </div>
                        </div>
                        
                        {/* Custom reason */}
                        <div>
                            <Label htmlFor="reason">Ou saisir un motif personnalisé</Label>
                            <Textarea
                                id="reason"
                                value={cancellationReason}
                                onChange={(e) => setCancellationReason(e.target.value)}
                                placeholder="Raison de l'annulation..."
                                className="mt-1"
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setCancelModal({ open: false, orderId: null });
                                setCancellationReason('');
                            }}
                        >
                            Retour
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={processing[cancelModal.orderId] || !cancellationReason.trim()}
                            onClick={() => handleStatusUpdate(cancelModal.orderId, 'cancelled', { cancellation_reason: cancellationReason || null })}
                        >
                            {processing[cancelModal.orderId] ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <XCircle className="w-4 h-4 mr-2" />
                            )}
                            Confirmer l'annulation
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* History Modal */}
            <Dialog open={historyModal.open} onOpenChange={(open) => !open && setHistoryModal({ open: false, orderId: null, history: [] })}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <History className="w-5 h-5 text-slate-600" />
                            Historique de la commande
                        </DialogTitle>
                    </DialogHeader>
                    <div className="max-h-96 overflow-y-auto py-4">
                        {historyModal.history.length === 0 ? (
                            <p className="text-center text-slate-500 py-8">Aucun historique disponible</p>
                        ) : (
                            <div className="space-y-3">
                                {historyModal.history.map((entry, idx) => (
                                    <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-2">
                                                <Badge variant="outline">{getStatusLabel(entry.old_status)}</Badge>
                                                <span className="text-slate-400">→</span>
                                                <Badge className="bg-blue-100 text-blue-800">{getStatusLabel(entry.new_status)}</Badge>
                                            </div>
                                        </div>
                                        <p className="text-xs text-slate-500">
                                            Par {entry.changed_by_email} le {formatDate(entry.created_at)}
                                        </p>
                                        {entry.tracking_number && (
                                            <p className="text-xs text-slate-600 mt-1">
                                                N° Suivi: <span className="font-mono">{entry.tracking_number}</span>
                                            </p>
                                        )}
                                        {entry.cancellation_reason && (
                                            <p className="text-xs text-red-600 mt-1">
                                                Motif: {entry.cancellation_reason}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
};

export default AdminOrders;
